/**
 * Profit & Loss Over Time — shadcn bar chart (signed P&L, green/red per bar).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  XAxis,
  YAxis,
  type BarShapeProps,
} from "recharts";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "next-themes";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  dashboardSidebarSkeletonClassName,
  dashboardSidebarSurfaceCardClassName,
} from "../../Dashboard/components/dashboardChartCardShell";
import { DashboardWidgetLoadingChart } from "../../Dashboard/components/DashboardWidgetLoadingCard";
import { getDashboardWidgetLoadingMessage } from "../../Dashboard/components/dashboardWidgetLoadingMessages";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  STATISTICS_NO_DATA_TIMEFRAME_MESSAGE,
  StatisticsChartTooltipPnlRow,
  statsChartTooltipContentClassName,
} from "./statisticsChartTooltip";
import { TradeStatisticsFilters } from "../../../../types/trade";
import { usePnLOverTime, useTradeStatistics } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrency } from "../../../../utils/formatDisplayCurrency";
import {
  formatChartHourAxisLabel,
  formatYmdBucketShortInZone,
  formatYmdRangeShortInZone,
  formatYmBucketShortInZone,
  parseStatisticsChartInstant,
  shouldIncludeYearOnChartAxis,
} from "../../../../utils/displayDateTime";
import {
  hourlyChartSpansMultipleDays,
  resolveChartTimeGrain,
} from "../../../../utils/chartDataSampling";
import {
  StatisticsCardHeadline,
  STATS_FONT_FAMILY,
  useStatsCardHeadlineColor,
} from "./statisticsCardHeadline";
import {
  statisticsShadcnBarChartAreaClassName,
  statisticsShadcnBarChartContainerClassName,
  statisticsShadcnCardClassName,
  statisticsShadcnCardContentClassName,
} from "./statisticsShadcnCardShell";
import { addCalendarDaysToYmd } from "../../../../utils/notificationPeriod";

/** Glacial green / neon red — aligned with Daily Trading Activity. */
const PNL_POSITIVE_COLOR = "#06E363";
const PNL_NEGATIVE_COLOR = "#F70009";
const BAR_CORNER_RADIUS = 4;
const BAR_SIZE_MIN = 18;
const BAR_SIZE_MAX = 140;
const PLOT_WIDTH_FALLBACK = 640;

/**
 * Size bars from measured plot width so sparse series (weekly) fill the chart.
 */
function getSignedBarChartLayout(
  pointCount: number,
  plotWidth: number
): {
  barSize: number;
  barCategoryGap: number;
  xAxisPadding: { left: number; right: number };
} {
  const n = Math.max(1, pointCount);
  // Approximate drawable width (Y-axis + margins are outside the band scale).
  const width = Math.max(200, plotWidth - 88);

  const categoryGapPx =
    n <= 6 ? 1 : n <= 12 ? 2 : n <= 20 ? 6 : n <= 40 ? 10 : 14;

  const totalGap = categoryGapPx * Math.max(0, n - 1);
  const bandWidth = (width - totalGap) / n;
  const barSize = Math.min(
    BAR_SIZE_MAX,
    Math.max(BAR_SIZE_MIN, Math.floor(bandWidth * 0.94))
  );

  const xAxisPadding =
    n <= 8
      ? { left: 0.04, right: 0.04 }
      : n <= 16
        ? { left: 0.02, right: 0.02 }
        : { left: 0.01, right: 0.01 };

  return { barSize, barCategoryGap: categoryGapPx, xAxisPadding };
}

const chartConfig = {
  pnl: { label: "Net P&L" },
  wins: { label: "Total Wins" },
  losses: { label: "Total Losses" },
  bestWeek: { label: "Best Week" },
  worstWeek: { label: "Worst Week" },
} satisfies ChartConfig;

const BREAKDOWN_DESCRIPTION: Record<
  NonNullable<PnLOverTimeChartProps["breakdown"]>,
  string
> = {
  hourly: "Net P&L by hour for the selected period",
  daily: "Net P&L by day for the selected period",
  weekly: "Net P&L by week for the selected period",
  monthly: "Net P&L by month for the selected period",
};

/** Keep only ISO week buckets that overlap the chart's local_start_date..local_end_date window. */
function filterWeeklyPointsToLocalRange<T extends { date: string }>(
  points: T[],
  localStart: string,
  localEnd: string,
  timeZone: string
): T[] {
  return points.filter((point) => {
    const weekStartYmd = String(point.date).slice(0, 10);
    const weekEndYmd = addCalendarDaysToYmd(weekStartYmd, 6, timeZone);
    return weekStartYmd <= localEnd && weekEndYmd >= localStart;
  });
}

function bestWorstWeekPnL(chartData: PnLChartDatum[]): { best: number; worst: number } {
  if (chartData.length === 0) {
    return { best: 0, worst: 0 };
  }
  let best = chartData[0].pnl;
  let worst = chartData[0].pnl;
  for (const row of chartData) {
    if (row.pnl > best) {
      best = row.pnl;
    }
    if (row.pnl < worst) {
      worst = row.pnl;
    }
  }
  return { best, worst };
}

function grossWinLossTotals(stats: {
  average_win?: number;
  average_loss?: number;
  winning_trades?: number;
  losing_trades?: number;
} | null): { totalWins: number; totalLosses: number } {
  if (!stats) {
    return { totalWins: 0, totalLosses: 0 };
  }
  const totalWins = (stats.average_win ?? 0) * (stats.winning_trades ?? 0);
  const totalLosses = Math.abs(stats.average_loss ?? 0) * (stats.losing_trades ?? 0);
  return {
    totalWins: Number.isFinite(totalWins) && totalWins > 0 ? totalWins : 0,
    totalLosses: Number.isFinite(totalLosses) && totalLosses > 0 ? totalLosses : 0,
  };
}

function PnLHeaderSummaryStat({
  label,
  amount,
  color,
  loading,
  withLeadingBorder = false,
  skeletonClassName,
}: {
  label: string;
  amount: number;
  color: string;
  loading: boolean;
  withLeadingBorder?: boolean;
  skeletonClassName?: string;
}) {
  const { displayCurrency } = useDisplayCurrency();

  return (
    <div
      className={cn(
        "relative flex min-w-[140px] flex-1 flex-col justify-center gap-1 border-t border-border px-[22px] py-4 text-left sm:border-t-0",
        withLeadingBorder && "sm:border-l"
      )}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      {loading ? (
        <Skeleton
          className={cn("mt-1 h-7 w-[100px] rounded-md", skeletonClassName)}
        />
      ) : (
        <span
          className="text-lg font-semibold leading-none tabular-nums tracking-tight"
          style={{ color }}
        >
          {formatDisplayCurrency(amount, displayCurrency, { locale: "en-US" })}
        </span>
      )}
    </div>
  );
}

type PnLChartDatum = {
  label: string;
  pnl: number;
  fill: string;
};

interface PnLOverTimeChartProps {
  filters?: TradeStatisticsFilters;
  breakdown?: "hourly" | "daily" | "monthly" | "weekly";
  /** Card title; defaults to “Profit & Loss Over Time”. */
  title?: string;
  /** Match dashboard sidebar card + skeleton chrome (trading activity day modal). */
  surfaceChrome?: "default" | "dashboard-sidebar";
  /**
   * When provided (e.g. page-level useTradeStatistics), reuse for win/loss header totals
   * instead of issuing a second /trades/statistics call.
   */
  statistics?: Parameters<typeof grossWinLossTotals>[0];
  statisticsLoading?: boolean;
}

function pnlBarFill(
  value: number,
  positive: string = PNL_POSITIVE_COLOR,
  negative: string = PNL_NEGATIVE_COLOR
): string {
  return value >= 0 ? positive : negative;
}

function pnlFromBarShape(value: BarShapeProps["value"], payload: unknown): number {
  const datum = payload as PnLChartDatum | undefined;
  if (typeof datum?.pnl === "number") {
    return datum.pnl;
  }
  if (typeof value === "number") {
    return value;
  }
  if (Array.isArray(value)) {
    return value[1] - value[0];
  }
  return 0;
}

/** Round only the outer end of each bar; keep the zero baseline edge square. */
function SignedPnlBar(props: BarShapeProps) {
  const { x, y, width, height, fill, value, payload } = props;
  const pnl = pnlFromBarShape(value, payload);

  const top = Math.min(y ?? 0, (y ?? 0) + height);
  const bottom = Math.max(y ?? 0, (y ?? 0) + height);
  const barHeight = bottom - top;

  if (width <= 0 || barHeight <= 0) {
    return null;
  }

  const r = Math.min(BAR_CORNER_RADIUS, width / 2, barHeight / 2);
  const left = x;
  const right = x + width;

  if (pnl >= 0) {
    const d = [
      `M ${left + r},${top}`,
      `H ${right - r}`,
      `Q ${right},${top} ${right},${top + r}`,
      `V ${bottom}`,
      `H ${left}`,
      `V ${top + r}`,
      `Q ${left},${top} ${left + r},${top}`,
      "Z",
    ].join(" ");
    return <path d={d} fill={fill} />;
  }

  const d = [
    `M ${left},${top}`,
    `H ${right}`,
    `V ${bottom - r}`,
    `Q ${right},${bottom} ${right - r},${bottom}`,
    `H ${left + r}`,
    `Q ${left},${bottom} ${left},${bottom - r}`,
    `V ${top}`,
    "Z",
  ].join(" ");
  return <path d={d} fill={fill} />;
}

const PnLOverTimeChart: React.FC<PnLOverTimeChartProps> = ({
  filters,
  breakdown = "monthly",
  title = "Profit & Loss Over Time",
  surfaceChrome = "default",
  statistics: statisticsOverride,
  statisticsLoading: statisticsLoadingOverride = false,
}) => {
  const isDashboardSidebarChrome = surfaceChrome === "dashboard-sidebar";
  const skeletonClassName = isDashboardSidebarChrome
    ? dashboardSidebarSkeletonClassName
    : undefined;
  const isWeeklyBreakdown = breakdown === "weekly";
  const { resolvedTheme } = useTheme();
  const displayTimeZone = useDisplayTimeZone();
  const { displayCurrency } = useDisplayCurrency();
  const errorColor = useColorModeValue("red.500", "red.300");
  const axisTickColor = useStatsCardHeadlineColor();
  const themeClass = resolvedTheme === "dark" ? "dark" : "";

  const { data, loading, error, errorAction } = usePnLOverTime(filters, breakdown);
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(error, errorAction);
  const needsOwnTradeStats = !isWeeklyBreakdown && statisticsOverride === undefined;
  const { statistics: fetchedTradeStats, loading: fetchedStatsLoading } = useTradeStatistics(
    needsOwnTradeStats ? filters : undefined,
    { enabled: needsOwnTradeStats }
  );
  const tradeStats = needsOwnTradeStats ? fetchedTradeStats : (statisticsOverride ?? null);
  const statsLoading = needsOwnTradeStats ? fetchedStatsLoading : statisticsLoadingOverride;

  const { totalWins, totalLosses } = useMemo(
    () => grossWinLossTotals(tradeStats),
    [tradeStats]
  );

  const chartData = useMemo((): PnLChartDatum[] => {
    if (!data?.data_points?.length) {
      return [];
    }

    let validDataPoints = data.data_points.filter(
      (point) => point && typeof point.total_pnl === "number" && point.date
    );

    if (
      breakdown === "weekly" &&
      filters?.local_start_date &&
      filters?.local_end_date
    ) {
      validDataPoints = filterWeeklyPointsToLocalRange(
        validDataPoints,
        filters.local_start_date,
        filters.local_end_date,
        filters.timezone ?? displayTimeZone
      );
    }

    if (validDataPoints.length === 0) {
      return [];
    }

    const pointDates = validDataPoints.map((point) => String(point.date));
    const timeGrain = resolveChartTimeGrain({
      responseGrain: data.breakdown,
      requestGrain: breakdown,
      dates: pointDates,
    });
    const hourlyIncludeDay =
      timeGrain === "hourly" && hourlyChartSpansMultipleDays(pointDates);

    const dateBuckets = validDataPoints.map((point) => {
      const raw = String(point.date);
      if (timeGrain === "hourly" || timeGrain === "daily" || timeGrain === "weekly") {
        return raw.length >= 10 ? raw.slice(0, 10) : raw;
      }
      if (timeGrain === "monthly") {
        return raw.length >= 7 ? raw.slice(0, 7) : raw;
      }
      return raw;
    });

    const includeYearOnAxis =
      timeGrain === "daily" || timeGrain === "weekly"
        ? shouldIncludeYearOnChartAxis(dateBuckets)
        : false;

    return validDataPoints.map((point) => {
      const raw = String(point.date);
      const pnl = Number(point.total_pnl);
      const safePnl = Number.isNaN(pnl) ? 0 : pnl;

      let label = raw;
      if (timeGrain === "hourly") {
        const parsed = parseStatisticsChartInstant(raw);
        label =
          parsed && !Number.isNaN(parsed.getTime())
            ? formatChartHourAxisLabel(parsed, displayTimeZone, {
                includeDay: hourlyIncludeDay,
              })
            : raw;
      } else if (timeGrain === "daily") {
        const ymd = raw.length >= 10 ? raw.slice(0, 10) : raw;
        label = formatYmdBucketShortInZone(ymd, displayTimeZone, {
          includeYear: includeYearOnAxis,
        });
      } else if (timeGrain === "weekly") {
        const startYmd = raw.length >= 10 ? raw.slice(0, 10) : raw;
        const endYmd = addCalendarDaysToYmd(startYmd, 6, displayTimeZone);
        label = formatYmdRangeShortInZone(startYmd, endYmd, displayTimeZone, {
          includeYear: includeYearOnAxis,
        });
      } else {
        const ym = raw.length >= 7 ? raw.slice(0, 7) : raw;
        label = formatYmBucketShortInZone(ym, displayTimeZone);
      }

      return {
        label,
        pnl: safePnl,
        fill: pnlBarFill(safePnl),
      };
    });
  }, [data, breakdown, displayTimeZone, filters?.local_start_date, filters?.local_end_date, filters?.timezone]);

  const { best: bestWeekPnL, worst: worstWeekPnL } = useMemo(
    () => bestWorstWeekPnL(chartData),
    [chartData]
  );

  const chartWrapRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(PLOT_WIDTH_FALLBACK);

  useEffect(() => {
    const el = chartWrapRef.current;
    if (!el) {
      return;
    }

    const updateWidth = () => {
      const width = el.getBoundingClientRect().width;
      if (width > 0) {
        setPlotWidth(width);
      }
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const barLayout = useMemo(
    () => getSignedBarChartLayout(chartData.length, plotWidth),
    [chartData.length, plotWidth]
  );

  const headerSummaryLoading = isWeeklyBreakdown ? loading : statsLoading;

  const cardHeader = (
    <CardHeader className="flex flex-col items-stretch border-b border-border !p-0 sm:flex-row">
      <div className="flex flex-1 flex-col justify-center gap-1 px-[22px] py-4 sm:py-5">
        <StatisticsCardHeadline>{title}</StatisticsCardHeadline>
        <CardDescription className="text-xs" style={{ fontFamily: STATS_FONT_FAMILY }}>
          {BREAKDOWN_DESCRIPTION[breakdown]}
        </CardDescription>
      </div>
      <div className="flex w-full sm:w-auto">
        {isWeeklyBreakdown ? (
          <>
            <PnLHeaderSummaryStat
              label={chartConfig.bestWeek.label}
              amount={bestWeekPnL}
              color={PNL_POSITIVE_COLOR}
              loading={headerSummaryLoading}
              skeletonClassName={skeletonClassName}
            />
            <PnLHeaderSummaryStat
              label={chartConfig.worstWeek.label}
              amount={worstWeekPnL}
              color={PNL_NEGATIVE_COLOR}
              loading={headerSummaryLoading}
              withLeadingBorder
              skeletonClassName={skeletonClassName}
            />
          </>
        ) : (
          <>
            <PnLHeaderSummaryStat
              label={chartConfig.wins.label}
              amount={totalWins}
              color={PNL_POSITIVE_COLOR}
              loading={headerSummaryLoading}
              skeletonClassName={skeletonClassName}
            />
            <PnLHeaderSummaryStat
              label={chartConfig.losses.label}
              amount={totalLosses}
              color={PNL_NEGATIVE_COLOR}
              loading={headerSummaryLoading}
              withLeadingBorder
              skeletonClassName={skeletonClassName}
            />
          </>
        )}
      </div>
    </CardHeader>
  );

  const cardShell = (children: React.ReactNode) => (
    <Card
      data-statistics-shadcn-card
      className={cn(
        statisticsShadcnCardClassName,
        isDashboardSidebarChrome && dashboardSidebarSurfaceCardClassName,
        themeClass
      )}
      style={{ fontFamily: STATS_FONT_FAMILY }}
    >
      {cardHeader}
      <CardContent className={cn(statisticsShadcnCardContentClassName, "pt-4")}>
        <div className={statisticsShadcnBarChartAreaClassName}>{children}</div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return cardShell(
      <DashboardWidgetLoadingChart
        ariaLabel={getDashboardWidgetLoadingMessage("pnlOverTime")}
        height={380}
      />
    );
  }

  if (showWidgetError && widgetError) {
    return cardShell(<StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} />);
  }

  if (chartData.length === 0) {
    return cardShell(
      <Box
        h="380px"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
          {STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
        </Text>
      </Box>
    );
  }

  return cardShell(
    <div ref={chartWrapRef} className="w-full">
      <ChartContainer
        config={chartConfig}
        className={statisticsShadcnBarChartContainerClassName}
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <BarChart
          accessibilityLayer
          data={chartData}
          barCategoryGap={barLayout.barCategoryGap}
          margin={{
            top: 16,
            right: 24,
            left: 16,
            bottom: 20,
          }}
        >
          <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
          <ReferenceLine y={0} className="stroke-border" strokeWidth={1} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={32}
            interval="preserveStartEnd"
            padding={barLayout.xAxisPadding}
            tick={{ fill: axisTickColor, fontSize: 11 }}
          />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          width={72}
          tick={{ fill: axisTickColor, fontSize: 12 }}
          tickFormatter={(value) =>
            formatDisplayCurrency(value, displayCurrency, { locale: "en-US" })
          }
        />
        <ChartTooltip
          cursor={{ fill: "hsl(var(--muted))", opacity: 0.25 }}
          content={
            <ChartTooltipContent
              className={statsChartTooltipContentClassName("w-[180px]")}
              labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
              hideIndicator
              labelFormatter={(_value, payload) => {
                const row = payload?.[0]?.payload as PnLChartDatum | undefined;
                return row?.label ?? "";
              }}
              formatter={(value) => {
                const n = Number(value);
                const color = pnlBarFill(n);
                return (
                  <StatisticsChartTooltipPnlRow
                    label="Net P&L"
                    value={formatDisplayCurrency(n, displayCurrency, { locale: "en-US" })}
                    color={color}
                  />
                );
              }}
            />
          }
        />
          <Bar dataKey="pnl" barSize={barLayout.barSize} shape={SignedPnlBar}>
            {chartData.map((entry) => (
              <Cell key={entry.label} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
};

export default PnLOverTimeChart;
